import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import type { CSSProperties } from "react";

/**
 * The section rail for a long edit form.
 *
 * The event drawer asks for roughly twenty fields — title through agenda, artwork,
 * video, Zoom link — and as one column that is a great deal of scrolling inside a
 * panel that is already an overlay. Splitting it into named sections means the
 * field you came to change is one click away instead of somewhere in the scroll.
 *
 * Both a rail and a stepper, because the two are used differently: someone editing
 * one field jumps straight to its section, while someone reviewing an event walks
 * through in order. Saving is deliberately not tied to reaching the end — this is
 * an edit form, not a wizard, so any section can be saved from.
 */
export type EventEditTab = {
  id: string;
  label: string;
};

export function EventEditTabRail({
  tabs,
  activeId,
  onChange,
}: {
  tabs: readonly EventEditTab[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  const activeIndex = Math.max(0, tabs.findIndex(tab => tab.id === activeId));

  return (
    <div
      role="tablist"
      aria-label="Event sections"
      style={{
        gridColumn: "1 / -1",
        display: "flex",
        flexWrap: "wrap",
        gap: ".35rem",
        padding: ".3rem",
        marginBottom: ".35rem",
        border: "1px solid rgba(255,255,255,.08)",
        borderRadius: ".8rem",
        background: "rgba(255,255,255,.025)",
      }}
    >
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeId;
        const isDone = index < activeIndex;
        const base: CSSProperties = {
          display: "inline-flex",
          alignItems: "center",
          gap: ".4rem",
          minHeight: 36,
          padding: ".4rem .7rem",
          border: isActive ? "1px solid rgba(233,30,140,.55)" : "1px solid transparent",
          borderRadius: ".6rem",
          background: isActive ? "linear-gradient(135deg,rgba(233,30,140,.22),rgba(125,234,255,.12))" : "transparent",
          color: isActive ? "#FFFFFF" : isDone ? "#9FF3FF" : "#93A2B7",
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "var(--chi-body-compact)",
          fontWeight: isActive ? 800 : 700,
          cursor: "pointer",
        };
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`event-edit-tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`event-edit-panel-${tab.id}`}
            onClick={() => onChange(tab.id)}
            style={base}
          >
            {/*
              A number rather than an icon: these are ordered sections, and the
              number is what makes "3 of 5" legible at a glance. A completed
              section swaps it for a tick so the rail also reads as progress.
            */}
            <span
              aria-hidden="true"
              style={{
                display: "grid",
                placeItems: "center",
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: isActive ? "#E91E8C" : isDone ? "rgba(125,234,255,.2)" : "rgba(255,255,255,.08)",
                color: isActive ? "white" : isDone ? "#9FF3FF" : "#93A2B7",
                fontSize: ".62rem",
                fontWeight: 800,
              }}
            >
              {isDone ? <Check size={11} strokeWidth={3} /> : index + 1}
            </span>
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function EventEditStepControls({
  tabs,
  activeId,
  onChange,
}: {
  tabs: readonly EventEditTab[];
  activeId: string;
  onChange: (id: string) => void;
}) {
  const index = Math.max(0, tabs.findIndex(tab => tab.id === activeId));
  const previous = tabs[index - 1];
  const next = tabs[index + 1];

  const button: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: ".35rem",
    minHeight: 38,
    padding: ".5rem .8rem",
    border: "1px solid rgba(125,234,255,.28)",
    borderRadius: ".6rem",
    background: "rgba(125,234,255,.06)",
    color: "#9FF3FF",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "var(--chi-body-compact)",
    fontWeight: 700,
    cursor: "pointer",
  };

  return (
    <div
      style={{
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        gap: ".55rem",
        paddingTop: ".35rem",
        borderTop: "1px solid rgba(255,255,255,.07)",
      }}
    >
      {previous && (
        <button type="button" onClick={() => onChange(previous.id)} style={button}>
          <ArrowLeft size={14} aria-hidden="true" /> {previous.label}
        </button>
      )}
      <span
        style={{
          marginLeft: previous ? 0 : "auto",
          marginRight: "auto",
          color: "#7F8BA0",
          fontFamily: "'Inter', sans-serif",
          fontSize: "var(--chi-body-compact)",
        }}
      >
        Section {index + 1} of {tabs.length}
      </span>
      {next && (
        <button type="button" onClick={() => onChange(next.id)} style={button}>
          {next.label} <ArrowRight size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
